import { api } from "@/lib/apiClient";

export type ProductLedgerLine = {
  id: number;
  date: string | null;
  voucher: string | null;
  voucherType: string | null;
  particular: string;
  description: string;
  accountId: number;
  accountName: string;
  accountCode: string;
  partyId: number | null;
  partyName: string | null;
  debit: number;
  credit: number;
};

export type ProductLedger = {
  product: { id: number; name: string };
  accounts: {
    id: number;
    name: string;
    code: string;
    accountTypeCode: string;
    accountTypeName: string;
    detailTypeCode: string;
    detailTypeName: string;
  }[];
  lines: ProductLedgerLine[];
  totals: {
    inventoryCost: number;
    supplierPayable: number;
    partnerReceivable: number;
    partnerCapital: number;
    vehicleCost: number;
    journalDebit: number;
    journalCredit: number;
    debit: number;
    credit: number;
    difference: number;
  };
};

type ApiProductLedger = {
  product?: { id?: number; name?: string };
  accounts?: {
    id?: number;
    name?: string;
    code?: string;
    account_type_code?: string;
    account_type_name?: string;
    detail_type_code?: string;
    detail_type_name?: string;
  }[];
  lines?: {
    id?: number;
    date?: string | null;
    voucher?: string | null;
    voucher_type?: string | null;
    particular?: string;
    description?: string;
    account_id?: number;
    account_name?: string;
    account_code?: string;
    party_id?: number | null;
    party_name?: string | null;
    debit?: number | string;
    credit?: number | string;
  }[];
  totals?: {
    inventory_cost?: number | string;
    supplier_payable?: number | string;
    partner_receivable?: number | string;
    partner_capital?: number | string;
    vehicle_cost?: number | string;
    journal_debit?: number | string;
    journal_credit?: number | string;
    debit?: number | string;
    credit?: number | string;
    difference?: number | string;
  };
};

export async function fetchProductLedger(productId: string | number): Promise<ProductLedger> {
  const response = await api.get<{ data: ApiProductLedger }>(`/api/v1/catalog/products/${productId}/ledger`);
  const raw = response.data.data ?? {};
  return {
    product: {
      id: Number(raw.product?.id ?? productId),
      name: raw.product?.name ?? "",
    },
    accounts: (raw.accounts ?? []).map((a) => ({
      id: Number(a.id ?? 0),
      name: a.name ?? "",
      code: a.code ?? "",
      accountTypeCode: a.account_type_code ?? "",
      accountTypeName: a.account_type_name ?? "",
      detailTypeCode: a.detail_type_code ?? "",
      detailTypeName: a.detail_type_name ?? "",
    })),
    lines: (raw.lines ?? []).map((line) => ({
      id: Number(line.id ?? 0),
      date: line.date ?? null,
      voucher: line.voucher ?? null,
      voucherType: line.voucher_type ?? null,
      particular: line.particular ?? line.account_name ?? "",
      description: line.description ?? "",
      accountId: Number(line.account_id ?? 0),
      accountName: line.account_name ?? "",
      accountCode: line.account_code ?? "",
      partyId: line.party_id != null ? Number(line.party_id) : null,
      partyName: line.party_name ?? null,
      debit: Number(line.debit ?? 0),
      credit: Number(line.credit ?? 0),
    })),
    totals: {
      inventoryCost: Number(raw.totals?.inventory_cost ?? 0),
      supplierPayable: Number(raw.totals?.supplier_payable ?? 0),
      partnerReceivable: Number(raw.totals?.partner_receivable ?? 0),
      partnerCapital: Number(raw.totals?.partner_capital ?? 0),
      vehicleCost: Number(raw.totals?.vehicle_cost ?? raw.totals?.inventory_cost ?? 0),
      journalDebit: Number(raw.totals?.journal_debit ?? raw.totals?.debit ?? 0),
      journalCredit: Number(raw.totals?.journal_credit ?? raw.totals?.credit ?? 0),
      debit: Number(raw.totals?.debit ?? raw.totals?.journal_debit ?? 0),
      credit: Number(raw.totals?.credit ?? raw.totals?.journal_credit ?? 0),
      difference: Number(raw.totals?.difference ?? 0),
    },
  };
}
